import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  useTheme,
  alpha,
  styled,
  ListSubheader,
  Snackbar,
  TableContainer
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import {
  FileDownload as ExportIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  DateRange as DateIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Person as PersonIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 0),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
    fontSize: '1.2rem'
  },
  '& .MuiTypography-root': {
    fontWeight: 600,
    color: theme.palette.primary.dark
  }
}));

const EmailChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  color: theme.palette.info.dark,
  '& .MuiChip-deleteIcon': {
    color: theme.palette.info.main
  }
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  maxHeight: '60vh',
  overflowY: 'auto',
  paddingRight: 0,
  marginRight: 0,
  scrollbarWidth: 'thin',
  scrollbarColor: theme.palette.mode === 'dark'
    ? '#555 #1e1e1e'
    : '#c1c1c1 #f1f1f1',
  '&::-webkit-scrollbar': {
    width: 8,
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark'
      ? '#1e1e1e'
      : '#f1f1f1',
    borderRadius: 0,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark'
      ? '#555'
      : '#c1c1c1',
    borderRadius: 4,
    border: `2px solid ${theme.palette.mode === 'dark' ? '#1e1e1e' : '#f1f1f1'}`,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.mode === 'dark'
      ? '#777'
      : '#a8a8a8',
  },
  direction: 'ltr',
  scrollbarGutter: 'stable',
}));

// Function to download blob as file
const downloadFile = (blob, filename) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Function to convert base64 to blob
const base64ToBlob = (base64, contentType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

// Function to send email with attachment
const sendEmailWithAttachment = async (fileBlob, filename, emails, dateRange) => {
  try {
    const formData = new FormData();
    formData.append('file', fileBlob, filename);
    formData.append('emails', emails.join(','));
    formData.append('subject', `Export Data - ${new Date().toLocaleDateString()}`);
    formData.append('message', `Please find attached the exported data for the period ${dateRange.startDate} to ${dateRange.endDate}.`);
    
    const response = await apiService.post(API_ENDPOINTS.SEND_EXPORT_EMAIL, formData, 'multipart/form-data');
    
    if (response?.success) {
      return true;
    } else {
      throw new Error(response?.message || 'Failed to send email');
    }
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

const AdvancedExportModal = ({ open, onClose, onRefresh }) => {
  const theme = useTheme();

  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [statusOptions, setStatusOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [dateFilter, setDateFilter] = useState('thisMonth');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [exportFormat, setExportFormat] = useState('excel');
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const showToast = (message, severity = 'success') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  useEffect(() => {
    if (open) {
      fetchFilterOptions();
      handleDateFilterChange('thisMonth');
    }
  }, [open]);

  const fetchFilterOptions = async () => {
    setLoading(true);
    setError('');
   
    try {
      const [statusChoicesRes, usersRes, companiesRes] = await Promise.all([
        apiService.get(API_ENDPOINTS.GET_order_status_choices),
        apiService.get(API_ENDPOINTS.GET_advance_export_users),
        apiService.get(API_ENDPOINTS.GET_advance_export_company)
      ]);

      if (statusChoicesRes?.success) {
        const statuses = Array.isArray(statusChoicesRes.data) ? statusChoicesRes.data : [];
        setStatusOptions(statuses.map(s => ({ name: s.name, category: s.category })));
      }

      if (usersRes?.success) {
        setUserOptions(usersRes.data);
      }

      if (companiesRes?.success) {
        setCompanyOptions(companiesRes.data?.Company || []);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      setError('Failed to load filter options. Please try again.');
      showToast('Failed to load filter options', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCategoriesByCompany = async () => {
      setCategoryOptions([]);
      setSelectedCategories([]);

      if (!selectedCompany) return;

      try {
        setCategoriesLoading(true);
        const res = await apiService.get(
          `${API_ENDPOINTS.GET_advance_export_product_category_by_company_id}?company=${selectedCompany}`
        );
        if (res?.success) {
          setCategoryOptions(res.data?.ProductCategory || []);
        }
      } catch (err) {
        console.error('Error fetching categories by company:', err);
        setCategoryOptions([]);
        showToast('Failed to load categories', 'error');
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategoriesByCompany();
  }, [selectedCompany]);

  const handleDateFilterChange = (filterType) => {
    setDateFilter(filterType);
    const today = new Date();
   
    switch (filterType) {
      case 'thisMonth':
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setDateRange({
          startDate: thisMonthStart.toISOString().split('T')[0],
          endDate: thisMonthEnd.toISOString().split('T')[0]
        });
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        setDateRange({
          startDate: lastMonthStart.toISOString().split('T')[0],
          endDate: lastMonthEnd.toISOString().split('T')[0]
        });
        break;
      case 'last3Months':
        const threeMonthsStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        const currentMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        setDateRange({
          startDate: threeMonthsStart.toISOString().split('T')[0],
          endDate: currentMonthEnd.toISOString().split('T')[0]
        });
        break;
      case 'custom':
        if (!dateRange.startDate || !dateRange.endDate) {
          setDateRange({
            startDate: today.toISOString().split('T')[0],
            endDate: today.toISOString().split('T')[0]
          });
        }
        break;
      default:
        break;
    }
  };

  const handleEmailAdd = () => {
    const email = emailInput.trim();
    if (email && isValidEmail(email) && !emails.includes(email)) {
      setEmails([...emails, email]);
      setEmailInput('');
    } else if (emails.includes(email)) {
      showToast('Email already exists', 'warning');
    } else if (!isValidEmail(email)) {
      showToast('Please enter a valid email', 'error');
    }
  };

  const handleEmailRemove = (emailToRemove) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      handleEmailAdd();
    }
  };

const handleExport = async () => {
  if (emails.length === 0) {
    setError('Please add at least one email address');
    showToast('Please add at least one email address', 'error');
    return;
  }

  setSubmitting(true);
  setError('');
  setSuccess('');

  try {
    const payload = {
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
      order_type: 'normal',
      user: selectedUsers.length > 0 ? selectedUsers.join(',') : 'None',
      company: selectedCompany || 'None',
      product_category: selectedCategories.length > 0 ? selectedCategories.join(',') : 'None',
      status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : 'None',
      emails: emails.join(','),
      format: exportFormat
    };

    const response = await apiService.postExportBlob(
      API_ENDPOINTS.POST_advance_export,
      payload,
      'application/json'
    );

    const body = response.data;
    const jsonErr = await apiService.getMessageIfExportBodyIsJsonError(body);
    if (jsonErr) {
      throw new Error(jsonErr);
    }
    if (!body || (body.byteLength !== undefined && body.byteLength === 0)) {
      throw new Error('No data received from server');
    }

    apiService.downloadBlobFromExportResponse(response);

    showToast('Export completed successfully! File downloaded and email sent.', 'success');
    setSuccess('Export completed successfully! File downloaded and email sent.');

    setTimeout(() => {
      handleClose();
      onRefresh?.();
    }, 2000);
  } catch (error) {
    console.error('Error submitting export:', error);
    let errorMessage = await apiService.getExportErrorMessage(error);
    if (errorMessage.includes("can't multiply sequence")) {
      errorMessage =
        'Server error: Invalid data format in order items. Our team has been notified. Please try again later or contact support.';
    }
    setError(errorMessage);
    showToast(errorMessage, 'error');
  } finally {
    setSubmitting(false);
  }
};

  const handleClose = () => {
    if (!submitting) {
      setSelectedStatuses([]);
      setSelectedUsers([]);
      setSelectedCategories([]);
      setSelectedCompany('');
      setEmails([]);
      setEmailInput('');
      setError('');
      setSuccess('');
      setDateFilter('thisMonth');
      setExportFormat('excel');
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExportIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Advanced Export - Products
              </Typography>
            </Box>
            <IconButton onClick={handleClose} disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Configure filters and export settings for product order data
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 1, px: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading filter options...</Typography>
            </Box>
          ) : (
            <ScrollableContent>
              <Box sx={{ mt: 1, px: 3 }}>
                {error && !toast.open && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && !toast.open && (
                  <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
                    {success}
                  </Alert>
                )}

                <Grid container spacing={3}>
                  {/* Date Range Filter */}
                  <Grid item xs={12}>
                    <StyledCard>
                      <CardContent>
                        <SectionHeader>
                          <DateIcon />
                          <Typography variant="h6">Date Range</Typography>
                        </SectionHeader>
                       
                        <FormControl component="fieldset" sx={{ mb: 2 }}>
                          <RadioGroup
                            row
                            value={dateFilter}
                            onChange={(e) => handleDateFilterChange(e.target.value)}
                            sx={{ gap: 2 }}
                          >
                            <FormControlLabel value="custom" control={<Radio />} label="Custom Range" />
                            <FormControlLabel value="thisMonth" control={<Radio />} label="This Month" />
                            <FormControlLabel value="lastMonth" control={<Radio />} label="Last Month" />
                            <FormControlLabel value="last3Months" control={<Radio />} label="Last 3 Months" />
                          </RadioGroup>
                        </FormControl>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Start Date"
                              type="date"
                              value={dateRange.startDate}
                              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                              disabled={dateFilter !== 'custom'}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="End Date"
                              type="date"
                              value={dateRange.endDate}
                              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                              InputLabelProps={{ shrink: true }}
                              disabled={dateFilter !== 'custom'}
                              inputProps={{ min: dateRange.startDate }}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </StyledCard>
                  </Grid>

                  {/* Filters */}
                  <Grid item xs={12} md={6}>
                    <StyledCard>
                      <CardContent>
                        <SectionHeader>
                          <FilterIcon />
                          <Typography variant="h6">Order Filters</Typography>
                        </SectionHeader>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                          💡 Leave any filter blank to include all records
                        </Typography>

                        {/* Status Filter */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "#121212" : "#fff",
                              px: 0.5,
                            }}
                          >
                            Order Status
                          </InputLabel>

                          <Select
                            multiple
                            value={selectedStatuses}
                            onChange={(e) => {
                              const value = e.target.value.filter(v => v !== "select-all");
                              setSelectedStatuses(value);
                            }}
                            renderValue={(selected) => (
                              <Box
                                sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {selected.map((value) => (
                                  <Chip
                                    key={value}
                                    label={value}
                                    size="small"
                                    sx={{ borderRadius: 1 }}
                                    deleteIcon={<CancelIcon />}
                                    onDelete={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setSelectedStatuses(
                                        selectedStatuses.filter(status => status !== value)
                                      );
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          >
                            <ListSubheader>
                              <Box sx={{ display: "flex", justifyContent: "space-between", px: 1 }}>
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const allStatuses = statusOptions.map(s => s.name);
                                    setSelectedStatuses(
                                      selectedStatuses.length === allStatuses.length ? [] : allStatuses
                                    );
                                  }}
                                >
                                  {selectedStatuses.length === statusOptions.length
                                    ? "Deselect All"
                                    : "Select All"}
                                </Button>

                                <Typography variant="caption" color="text.secondary">
                                  {selectedStatuses.length} of {statusOptions.length} selected
                                </Typography>
                              </Box>
                            </ListSubheader>

                            <Divider />

                            {statusOptions.map((status) => (
                              <MenuItem key={status.name} value={status.name}>
                                {status.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Company Filter */}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                          <InputLabel
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "#121212" : "#fff",
                              px: 0.5,
                            }}
                          >
                            Company
                          </InputLabel>
                          <Select
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                          >
                            {companyOptions.map((company) => (
                              <MenuItem key={company.id} value={company.id}>
                                {company.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Category Filter */}
                        <FormControl fullWidth>
                          <InputLabel
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "#121212" : "#fff",
                              px: 0.5,
                            }}
                          >
                            Product Categories
                          </InputLabel>
                          <Select
                            multiple
                            value={selectedCategories}
                            onChange={(e) => {
                              const value = e.target.value;
                              const filteredValue = value.filter(v => v !== 'select-all');
                              setSelectedCategories(filteredValue);
                            }}
                            disabled={!selectedCompany || categoriesLoading}
                            renderValue={(selected) => (
                              <Box
                                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                {selected.map((value) => {
                                  const category = categoryOptions.find(cat => cat.id === value);
                                  return (
                                    <Chip
                                      key={value}
                                      label={category?.name || value}
                                      size="small"
                                      onDelete={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        e.nativeEvent.stopImmediatePropagation();
                                        setSelectedCategories(selectedCategories.filter(id => id !== value));
                                      }}
                                      deleteIcon={<CancelIcon />}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            <ListSubheader
                              sx={{
                                backgroundColor: 'transparent',
                                py: 1,
                                px: 2
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const allCategoryIds = categoryOptions.map(cat => cat.id);
                                    setSelectedCategories(
                                      selectedCategories.length === categoryOptions.length ? [] : allCategoryIds
                                    );
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    minWidth: 'auto',
                                    px: 1.5
                                  }}
                                >
                                  {selectedCategories.length === categoryOptions.length ? 'Deselect All' : 'Select All'}
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                  {selectedCategories.length} of {categoryOptions.length} selected
                                </Typography>
                              </Box>
                            </ListSubheader>
                            <Divider />
                            {categoryOptions.map((category) => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </CardContent>
                    </StyledCard>
                  </Grid>

                  {/* Users & Export Settings */}
                  <Grid item xs={12} md={6}>
                    <StyledCard>
                      <CardContent>
                        <SectionHeader>
                          <PersonIcon />
                          <Typography variant="h6">Export</Typography>
                        </SectionHeader>

                        <FormControl fullWidth>
                          <InputLabel
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === "dark" ? "#121212" : "#fff",
                              px: 0.5,
                            }}
                          >
                            Export Format
                          </InputLabel>
                          <Select
                            value={exportFormat}
                            onChange={(e) => setExportFormat(e.target.value)}
                          >
                            <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                            <MenuItem value="csv">CSV (.csv)</MenuItem>
                          </Select>
                        </FormControl>
                      </CardContent>
                    </StyledCard>
                  </Grid>

                  {/* Email Configuration */}
                  <Grid item xs={12}>
                    <StyledCard>
                      <CardContent>
                        <SectionHeader>
                          <EmailIcon />
                          <Typography variant="h6">Email Configuration</Typography>
                        </SectionHeader>

                        <TextField
                          fullWidth
                          label="Add Email Address"
                          value={emailInput}
                          onChange={(e) => setEmailInput(e.target.value)}
                          onKeyPress={handleEmailKeyPress}
                          placeholder="Enter email and press Enter or comma"
                          helperText="Press Enter or comma to add multiple emails"
                          InputProps={{
                            endAdornment: (
                              <Button
                                size="small"
                                onClick={handleEmailAdd}
                                disabled={!emailInput.trim() || !isValidEmail(emailInput.trim())}
                              >
                                Add
                              </Button>
                            )
                          }}
                        />

                        {emails.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Email Recipients ({emails.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {emails.map((email, index) => (
                                <EmailChip
                                  key={index}
                                  label={email}
                                  onDelete={() => handleEmailRemove(email)}
                                  deleteIcon={<CancelIcon />}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </Grid>
              </Box>
            </ScrollableContent>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={submitting || loading || emails.length === 0}
            startIcon={submitting ? <CircularProgress size={16} /> : <DownloadIcon />}
            sx={{ textTransform: 'none', minWidth: 120 }}
          >
            {submitting ? 'Processing...' : 'Export & Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={handleToastClose}
          severity={toast.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default AdvancedExportModal;